import "./MentionedVideoThumbnail.css";
import { CoverButton } from "@ui/CoverButton.tsx";
import { formatDurationClockShort } from "@ui/format.ts";
import { usePendingImage } from "@ui/hooks/usePendingImage";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { scrollCarouselSlideIntoView } from "@ui/molecules/Carousel/Carousel";
import { Pill } from "@ui/Pill.tsx";
import { onMount, Show } from "solid-js";
import {
	installMentionClipboard,
	videoMentionUrl,
} from "../utils/mentionClipboard";
import {
	isMentionClip,
	mentionClipDurationSec,
	type PlayVideoMentionsHandler,
	playVideoMentions,
} from "../utils/videoMentionPlayback.ts";
import {
	addVideoMentionToClipboard,
	isVideoMentionInClipboard,
} from "./clipboard/clipboardStore.ts";
import type { MentionedVideoItem } from "./VideoMention.tsx";
import { VideoMentionMarker } from "./VideoMentionMarker.tsx";

type MentionedVideoThumbnailProps = {
	videoMention: MentionedVideoItem;
	allVideoMentions: MentionedVideoItem[];
	appEmbedded: boolean;
	offersClipboard?: boolean;
	/**
	 * `float`: right/left floated block beside a paragraph (single portrait mention).
	 * `carousel`: fixed-height slide inside the `MentionedVideos` carousel.
	 */
	variant?: "float" | "carousel";
	floatSide?: "left" | "right";
	onPlayVideoMentions?: PlayVideoMentionsHandler;
};

// Holds the thumbnail's size, and so its play target, while the image is
// pending; the broken-link mark appears only once there is no image to come.
const MentionedVideoThumbnailPlaceholder = (props: {
	broken: boolean;
	aspectRatio?: number | null;
}) => (
	<span
		class="mentioned-video-thumbnail__placeholder"
		aria-hidden="true"
		style={
			props.aspectRatio && props.aspectRatio > 0
				? {
						"--mentioned-video-thumbnail-aspect-ratio": String(
							props.aspectRatio,
						),
					}
				: undefined
		}
	>
		<Show when={props.broken}>
			<MaterialSymbol symbol="link_off" color="fg3" relativeSize={2.25} />
		</Show>
	</span>
);

export const MentionedVideoThumbnail = (
	props: MentionedVideoThumbnailProps,
) => {
	// Copying a thumbnail writes the link below in the QuestionBox's own form, so
	// that the clip survives being pasted into another app (see mentionClipboard).
	onMount(installMentionClipboard);

	const variant = () => props.variant ?? "float";
	const thumbnail = usePendingImage(() => props.videoMention.thumbnail_url);

	const offersClipboard = () => props.offersClipboard === true;
	const held = () => isVideoMentionInClipboard(props.videoMention);

	let rootEl: HTMLDivElement | undefined;

	const playVideo = () => {
		playVideoMentions(props.allVideoMentions, props.videoMention.id, {
			appEmbedded: props.appEmbedded,
			onPlayVideoMentions: props.onPlayVideoMentions,
		});
		// Opening the panel can narrow the chat column, so measure where the
		// thumbnail ended up only once that reflow has happened.
		requestAnimationFrame(() => {
			if (rootEl) {
				scrollCarouselSlideIntoView(rootEl);
			}
		});
	};

	return (
		<div
			ref={rootEl}
			class="mentioned-video-thumbnail"
			data-video-mention-id={props.videoMention.id}
			// The thumbnail is a play control that may hold an add button, so the
			// clip's `/mention` link is carried here for the copy handler to read.
			data-mention-href={videoMentionUrl(props.videoMention)}
			classList={{
				"mentioned-video-thumbnail--float": variant() === "float",
				"mentioned-video-thumbnail--float-right":
					variant() === "float" && props.floatSide === "right",
				"mentioned-video-thumbnail--carousel": variant() === "carousel",
			}}
		>
			<span class="mentioned-video-thumbnail__media">
				<Show
					when={thumbnail.src()}
					fallback={
						<MentionedVideoThumbnailPlaceholder
							broken={thumbnail.failed() || !props.videoMention.thumbnail_url}
							aspectRatio={props.videoMention.aspect_ratio}
						/>
					}
				>
					{(src) => (
						<img class="mentioned-video-thumbnail__image" src={src()} alt="" />
					)}
				</Show>
				{/* Before the pill, so the pill's own clicks win where they overlap. */}
				<CoverButton
					label={`Play video ${props.videoMention.index}`}
					onActivate={playVideo}
				/>
				{/*
				 * The whole pill is the add control where there is a clipboard: the
				 * `+` marks what it does rather than taking the click itself, which
				 * a chip this small is a poor target for. Elsewhere the pill is the
				 * label it reads as.
				 */}
				<Pill
					class="mentioned-video-thumbnail__pill video-mention-pill"
					as={offersClipboard() ? "button" : "span"}
					theme="dark"
					size="small"
					aria-label={
						offersClipboard()
							? `Add video ${props.videoMention.index}`
							: undefined
					}
					onClick={
						offersClipboard()
							? () => addVideoMentionToClipboard(props.videoMention)
							: undefined
					}
				>
					<VideoMentionMarker
						index={props.videoMention.index}
						clip={isMentionClip(props.videoMention)}
						theme="dark"
					/>
					{formatDurationClockShort(mentionClipDurationSec(props.videoMention))}
					<Show when={offersClipboard()}>
						<MaterialSymbol
							class="mentioned-video-thumbnail__add-symbol"
							classList={{
								"mentioned-video-thumbnail__add-symbol--held": held(),
							}}
							symbol={held() ? "check" : "add"}
						/>
					</Show>
				</Pill>
			</span>
		</div>
	);
};
